import { forwardRef, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MessageCircle, X, Instagram, Facebook } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';

const TikTokIcon = forwardRef<SVGSVGElement, React.SVGProps<SVGSVGElement>>((props, ref) => (
  <svg ref={ref} viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
));
TikTokIcon.displayName = 'TikTokIcon';

const socialLinks = [
  { icon: Instagram, href: 'https://www.instagram.com/raven__moon__photography?igsh=MTYwdGhnOW5wcnF3Nw==', label: 'Instagram' },
  { icon: Facebook, href: 'https://www.facebook.com/share/188TB5SuQ6/', label: 'Facebook' },
  { icon: TikTokIcon, href: 'https://www.tiktok.com/@raven_moon_photography?_r=1&_t=ZP-92dXV8YF0HP', label: 'TikTok', isCustom: true },
];

export const StickyConsultationBar = forwardRef<unknown, object>(function StickyConsultationBar(_, _ref) {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const location = useLocation();

  const hideOnPaths = ['/booking', '/cart', '/admin', '/auth', '/contact'];
  const shouldHidePage = hideOnPaths.some(path => location.pathname.startsWith(path));

  useEffect(() => {
    if (isDismissed) return;
    const handleScroll = () => setIsVisible(window.scrollY > 500);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isDismissed]);

  if (shouldHidePage || isDismissed) return null;

  return (
    <div
      className={`fixed bottom-20 right-4 z-50 transition-all duration-500 ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0 pointer-events-none'
      }`}
    >
      <div className="relative w-[220px] rounded-2xl border border-primary/20 bg-background/95 backdrop-blur-2xl shadow-[0_8px_40px_-12px_hsl(var(--primary)/0.25)] overflow-hidden">
        {/* Dismiss */}
        <button
          onClick={() => setIsDismissed(true)}
          className="absolute top-2 right-2 z-10 rounded-full p-1 text-muted-foreground/60 transition-colors hover:text-foreground hover:bg-card/60"
          aria-label="Dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>

        {/* Top accent line */}
        <div className="h-[2px] bg-gradient-gold" />

        <div className="p-5 text-center space-y-4">
          {/* CTA */}
          <div>
            <MessageCircle className="h-5 w-5 text-primary mx-auto mb-2" />
            <p className="font-display text-sm text-foreground leading-snug mb-1">Free Consultation</p>
            <p className="text-[11px] text-muted-foreground leading-tight">Let's chat about your vision</p>
          </div>

          <Link
            to="/contact?consultation=true"
            onClick={() => trackEvent('cta_click', { cta_name: 'sticky_consultation_bar', cta_text: 'Book Free Call' })}
            className="inline-flex w-full min-h-[40px] items-center justify-center rounded-full bg-gradient-gold px-4 py-2 font-cinzel text-[10px] tracking-[0.2em] uppercase text-primary-foreground shadow-ethereal transition-all hover:shadow-gold"
          >
            Book Free Call
          </Link>

          {/* Social links */}
          <div className="pt-1">
            <p className="font-cinzel text-[9px] tracking-[0.2em] uppercase text-muted-foreground/70 mb-2">Follow Along</p>
            <div className="flex items-center justify-center gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  className="text-foreground/40 hover:text-primary transition-all duration-300"
                  aria-label={social.label}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {'isCustom' in social ? <social.icon /> : <social.icon className="h-4 w-4" />}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

StickyConsultationBar.displayName = 'StickyConsultationBar';
